/**
 * Nether fortress (simplified vanilla layout, analytic so every chunk can draw
 * its own slice deterministically without neighbour data):
 *
 *   one fortress per 320x320 region (offset randomly inside it), deck at
 *   y 48..67; a 15x15 blaze-spawner hall in the middle (raised 3x3 dais with
 *   the spawner), four 5-wide nether-brick bridges running out 36..68 blocks
 *   with 1-high railings, arched pillars every 12 blocks down to the ground,
 *   a nether wart garden (soul sand beds) at the end of the east arm, a loot
 *   chest room at the end of the west arm, a lava-well room at the end of the
 *   south arm and the bridges' headroom carved through any netherrack.
 *   The first 12 blocks of every arm are enclosed corridors (walls, roof,
 *   nether-brick-fence windows like vanilla's corridor pieces); the north arm
 *   ends in a staircase down 6 blocks to a lower landing.
 */

import { CHUNK_SIZE } from '../constants';
import {
  AIR, BEDROCK_ID, CHEST_FACING_0, CHISELED_NETHER_BRICKS_ID, CRACKED_NETHER_BRICKS_ID, LAVA_ID,
  NETHER_BRICKS_ID, NETHER_WART_IDS, SOUL_SAND_ID, SPAWNER_ID, NETHER_BRICK_FENCE_ID, NETHER_BRICK_STAIRS_IDS,
} from '../blocks';
import { hash2 } from '../noise';

export const FORTRESS_REGION = 320;
const HALL = 7; // half-size of the central hall (15x15)
const ROOM = 4; // half-size of the end rooms (9x9)

export interface FortressPlan {
  x: number; z: number; y: number;
  /** Arm lengths from the centre: +X, -X, +Z, -Z. */
  arms: [number, number, number, number];
}

const planCache = new Map<string, FortressPlan>();

/** The fortress of the region containing (x, z). */
export function fortressPlan(seed: number, x: number, z: number): FortressPlan {
  const rx = Math.floor(x / FORTRESS_REGION), rz = Math.floor(z / FORTRESS_REGION);
  const key = rx + ',' + rz;
  let p = planCache.get(key);
  if (p) return p;
  const s = seed ^ 0x0f0e7;
  const h = (k: number): number => hash2(rx * 31 + k, rz * 17 - k, s);
  const arm = (k: number): number => 36 + Math.floor(h(k) * 33);
  p = {
    x: rx * FORTRESS_REGION + 80 + Math.floor(h(1) * (FORTRESS_REGION - 160)),
    z: rz * FORTRESS_REGION + 80 + Math.floor(h(2) * (FORTRESS_REGION - 160)),
    y: 48 + Math.floor(h(3) * 20),
    arms: [arm(4), arm(5), arm(6), arm(7)],
  };
  if (planCache.size > 64) planCache.clear();
  planCache.set(key, p);
  return p;
}

/** Is (x, y, z) inside the fortress' footprint (for its mob spawn list)? */
export function inFortress(seed: number, x: number, y: number, z: number): boolean {
  const p = fortressPlan(seed, x, z);
  if (y < p.y - 2 || y > p.y + 7) return false;
  return cellKind(p, x - p.x, z - p.z) !== 0;
}

/** 0 = outside, 1 = bridge deck, 2 = hall, 3 = garden, 4 = chest room, 5 = lava well. */
function cellKind(p: FortressPlan, dx: number, dz: number): number {
  if (Math.abs(dx) <= HALL && Math.abs(dz) <= HALL) return 2;
  const [px, nx, pz] = p.arms;
  if (Math.abs(dz) <= ROOM && Math.abs(dx - px) <= ROOM) return 3;
  if (Math.abs(dz) <= ROOM && Math.abs(dx + nx) <= ROOM) return 4;
  if (Math.abs(dx) <= ROOM && Math.abs(dz - pz) <= ROOM) return 5;
  const nz = p.arms[3];
  if (Math.abs(dz) <= 2 && dx > 0 && dx <= px) return 1;
  if (Math.abs(dz) <= 2 && dx < 0 && dx >= -nx) return 1;
  if (Math.abs(dx) <= 2 && dz > 0 && dz <= pz) return 1;
  if (Math.abs(dx) <= 2 && dz < 0 && dz >= -nz) return 1;
  return 0;
}

const AREA = CHUNK_SIZE * CHUNK_SIZE;

/** Draw this chunk's slice of the fortress into `out` (after terrain + features). */
export function placeFortress(seed: number, cx: number, cz: number, out: Uint16Array): void {
  const ox = cx * CHUNK_SIZE, oz = cz * CHUNK_SIZE;
  const p = fortressPlan(seed, ox + 8, oz + 8);
  const reach = Math.max(...p.arms) + ROOM + 2;
  if (ox + 16 < p.x - reach || ox > p.x + reach || oz + 16 < p.z - reach || oz > p.z + reach) return;
  const brick = (x: number, z: number, yy: number): number => {
    const r = hash2(x * 3 + yy, z * 5 - yy, seed ^ 0xb21c);
    return r < 0.06 ? CRACKED_NETHER_BRICKS_ID : NETHER_BRICKS_ID;
  };
  for (let lz = 0; lz < CHUNK_SIZE; lz++) {
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const wx = ox + lx, wz = oz + lz;
      const dx = wx - p.x, dz = wz - p.z;
      const kind = cellKind(p, dx, dz);
      if (kind === 0) continue;
      const col = lx + lz * CHUNK_SIZE;
      const set = (yy: number, id: number): void => {
        if (yy > 0 && yy < 126 && out[col + yy * AREA] !== BEDROCK_ID) out[col + yy * AREA] = id;
      };
      // the north arm's last 8 blocks step down 6 (stairs), ending on a landing
      const north = kind === 1 && Math.abs(dx) <= 2 && dz < -HALL;
      const stairStart = p.arms[3] - 8;
      const down = north ? Math.max(0, Math.min(6, -dz - stairStart)) : 0;
      const y = p.y - down;
      // deck + headroom
      set(y, brick(wx, wz, y));
      set(y - 1, brick(wx, wz, y - 1));
      for (let k = 1; k <= 5; k++) set(y + k, AIR);

      if (kind === 1) {
        const alongX = Math.abs(dz) <= 2 && Math.abs(dx) > HALL;
        const across = alongX ? dz : dx;
        const along = alongX ? Math.abs(dx) : Math.abs(dz);
        if (north && down > 0 && down < 6 && Math.abs(across) < 2 && -dz - stairStart === down) {
          // a stair tread descending north (ascending back toward the hall, facing +Z)
          set(y, NETHER_BRICK_STAIRS_IDS[0]);
          set(y - 1, brick(wx, wz, y - 1));
        }
        if (along <= HALL + 12) {
          // enclosed corridor: walls with fence windows every other block, roof
          if (Math.abs(across) === 2) {
            set(y + 1, brick(wx, wz, y + 1));
            set(y + 2, brick(wx, wz, y + 2));
            set(y + 3, along % 2 === 0 ? NETHER_BRICK_FENCE_ID : brick(wx, wz, y + 3));
            set(y + 4, brick(wx, wz, y + 4));
          }
          set(y + 5, brick(wx, wz, y + 5));
        } else if (Math.abs(across) === 2) set(y + 1, along % 3 === 0 ? brick(wx, wz, y + 1) : NETHER_BRICK_FENCE_ID); // railing
        // arched pillars: a 3-wide column every 12 blocks down to solid ground
        const ph = along % 12;
        if (ph >= 5 && ph <= 7 && Math.abs(across) <= 2) {
          const archTop = ph === 6 ? 3 : 2;
          for (let yy = y - archTop; yy > 1; yy--) {
            const id = out[col + yy * AREA];
            if (id !== AIR && id !== LAVA_ID) break;
            set(yy, brick(wx, wz, yy));
          }
        } else if (Math.abs(across) === 2 && (ph === 4 || ph === 8)) set(y - 2, brick(wx, wz, y - 2));
        continue;
      }
      const half = kind === 2 ? HALL : ROOM;
      const ux = kind === 2 ? dx : kind === 3 ? dx - p.arms[0] : kind === 4 ? dx + p.arms[1] : dx;
      const uz = kind === 5 ? dz - p.arms[2] : dz;
      const wall = Math.abs(ux) === half || Math.abs(uz) === half;
      // a room's walls (fence windows at head height; doorways where the bridges join)
      const door = (Math.abs(uz) <= 1 && Math.abs(ux) === half) || ((kind === 2 || kind === 5) && Math.abs(ux) <= 1 && Math.abs(uz) === half);
      if (wall && !door) {
        for (let k = 1; k <= 5; k++) set(y + k, k === 3 && (ux + uz) % 2 === 0 ? NETHER_BRICK_FENCE_ID : brick(wx, wz, y + k));
        set(y + 6, brick(wx, wz, y + 6));
      }
      // foundations under the rooms
      for (let yy = y - 2; yy > 1; yy--) {
        const id = out[col + yy * AREA];
        if (id !== AIR && id !== LAVA_ID) break;
        if (wall) set(yy, brick(wx, wz, yy));
      }
      if (kind === 2) {
        if (Math.abs(dx) <= 1 && Math.abs(dz) <= 1) set(y + 1, dx === 0 && dz === 0 ? CHISELED_NETHER_BRICKS_ID : brick(wx, wz, y + 1));
        if (dx === 0 && dz === 0) set(y + 2, SPAWNER_ID);
      } else if (kind === 3) {
        // wart garden: soul sand beds with nether wart in the inner 5x5
        if (Math.abs(ux) <= 2 && Math.abs(dz) <= 2 && Math.abs(dz) !== 0) {
          set(y, SOUL_SAND_ID);
          const age = Math.floor(hash2(wx, wz, seed ^ 0x3a7) * 4);
          set(y + 1, NETHER_WART_IDS[age]);
        }
      } else if (kind === 4 && ux === -(ROOM - 1) && dz === 0) {
        set(y + 1, CHEST_FACING_0);
      } else if (kind === 5 && Math.abs(ux) <= 1 && Math.abs(uz) <= 1) {
        // vanilla's lava-well room: a 3x3 lava pool sunk into the floor, brick-lined
        set(y, LAVA_ID);
        set(y - 1, brick(wx, wz, y - 1));
      }
      if (!wall) set(y + 6, brick(wx, wz, y + 6)); // room roof
    }
  }
}
